// User access: the filtering, grouping and safety checks behind the table, kept pure so they
// test without the page.

/** A project by name, or by id when its `projects` row is gone. */
export const projectLabel = (row) => row.project_name || `Project #${row.project_id}`;

const byText = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/**
 * The assignments that match the search (email, company, project name or id) and the role
 * filter. The backend sends only the projects 123bimlive shows.
 */
export function filterAssignments(rows, { query = '', roleId = null }) {
  const q = query.trim().toLowerCase();
  const id = q.replace(/^#/, '');
  return rows.filter((row) => {
    if (roleId && row.role_id !== roleId) return false;
    if (!q) return true;
    return (row.email || '').toLowerCase().includes(q)
      || projectLabel(row).toLowerCase().includes(q)
      || (/^\d+$/.test(id) && String(row.project_id) === id);
  });
}

/**
 * The accounts on no project ({email}) that match the search by email. A role filter hides
 * them all: they hold no role anywhere.
 */
export function filterUnassigned(people, { query = '', roleId = null }) {
  if (roleId) return [];
  const q = query.trim().toLowerCase();
  return people.filter((p) => !q || (p.email || '').toLowerCase().includes(q));
}

const byEmail = (a, b) => byText(a.email || '', b.email || '');

/**
 * Rows grouped for the table's tree. `by` 'person' gives one group per email (its projects
 * in name order), 'project' one per project (its people in email order), 'role' one per role,
 * Administrator first (its people, then their projects, in order). `dir` 'desc' reverses the
 * order of the groups. `unassigned` ({email}: accounts on no project) adds, by person, a
 * zero-project group per account in order among the others (`unassigned: true`); by project
 * or role, one "No project" group (key 'none', `people`) that stays last whatever the order.
 */
export function groupAssignments(rows, by, dir = 'asc', unassigned = []) {
  const flip = dir === 'desc' ? -1 : 1;
  const SHAPES = {
    person: {
      key: (r) => (r.email || '').toLowerCase(),
      label: (r) => r.email,
      inner: (a, b) => byText(projectLabel(a), projectLabel(b)),
      order: (a, b) => byText(a.label, b.label),
    },
    project: {
      key: (r) => `p${r.project_id}`,
      label: projectLabel,
      inner: (a, b) => byText(a.email, b.email),
      order: (a, b) => byText(a.label, b.label),
    },
    role: {
      key: (r) => `r${r.role_id}`,
      label: (r) => r.role_label,
      inner: (a, b) => byText(a.email, b.email) || byText(projectLabel(a), projectLabel(b)),
      order: (a, b) => a.sample.role_id - b.sample.role_id,
    },
  };
  const shape = SHAPES[by];
  const groups = new Map();
  rows.forEach((row) => {
    const key = shape.key(row);
    if (!groups.has(key)) groups.set(key, { key, label: shape.label(row), sample: row, rows: [] });
    groups.get(key).rows.push(row);
  });
  if (by === 'person') {
    unassigned.forEach((p) => {
      const key = (p.email || '').toLowerCase();
      if (!groups.has(key)) groups.set(key, { key, label: p.email, sample: null, rows: [], unassigned: true });
    });
  }
  const out = [...groups.values()]
    .map((g) => ({ ...g, rows: g.rows.sort(shape.inner) }))
    .sort((a, b) => flip * shape.order(a, b));
  if (by !== 'person' && unassigned.length) {
    out.push({ key: 'none', label: 'No project', sample: null, rows: [], people: [...unassigned].sort(byEmail), unassigned: true });
  }
  return out;
}

/** True when `row` is the only Administrator of its project (removing or demoting them leaves none). */
export function onlyAdministrator(rows, row) {
  if (row.role_id !== 1) return false;
  return !rows.some((r) => r.project_id === row.project_id && r.role_id === 1 && r.user_id !== row.user_id);
}

export const PICK_LIMIT = 50;

/**
 * The grant form's project picker: the projects whose name contains `query`, or whose number
 * starts with it ("12"), or is exactly it ("#12"), in name order, at most `limit` of them
 * ({matches, more}: how many more matched). An empty query matches every project.
 */
export function matchProjects(query, projects, limit = PICK_LIMIT) {
  const q = (query || '').trim().toLowerCase();
  const exact = q.startsWith('#');
  const id = q.replace(/^#/, '');
  const byNumber = /^\d+$/.test(id);
  const numberMatch = (p) => byNumber && (exact ? String(p.id) === id : String(p.id).startsWith(id));
  const all = projects
    .filter((p) => !q || (p.name || '').toLowerCase().includes(q) || numberMatch(p))
    .sort((a, b) => byText(a.name || '', b.name || '') || a.id - b.id);
  return { matches: all.slice(0, limit), more: Math.max(0, all.length - limit) };
}

const names = (projects) => projects.map((p) => p.name || `Project #${p.id}`).join(', ');

/**
 * The notice after granting: `outcome` = {granted: [project], already: [project],
 * failed: [[project, reason]]}. One project granted reads as a sentence; several as a count.
 * Nothing granted points at the list, where the role can be changed.
 */
export function describeGrant(email, roleLabel, { granted, already, failed }) {
  const parts = [];
  if (granted.length === 1) parts.push(`${email} now has ${roleLabel} access to ${names(granted)}.`);
  else if (granted.length) parts.push(`${email} now has ${roleLabel} access to ${granted.length} projects.`);
  else if (already.length && !failed.length) {
    return `${email} already has access to ${names(already)}. Change their role in the list below.`;
  }
  if (already.length) parts.push(`Already had access to ${names(already)}.`);
  failed.forEach(([p, reason]) => parts.push(`Failed on ${names([p])}: ${reason}.`));
  return parts.join(' ');
}
